import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import MobileHeader from './MobileHeader';
import AppSidebar from '../app-sidebar/AppSidebar';
import { usePermissions } from '../../hooks/usePermissions';
import { useLayout } from '../../contexts/LayoutContext';
import { useAuth } from '../../contexts/AuthContext';
import { RefuseTo } from '../common/NoAccess';
import OfflineBanner from '../common/OfflineBanner';
import { BootLoading } from '../common/ProtectedRoute';
import { ChunkErrorBoundary } from '../common/ChunkErrorBoundary';

// V17.520 (Phase 0c, brief 2026-10-26-001, F29): which header to MOUNT. Both headers carry a
// notification bell that polls /notifications, so mounting both and hiding one with CSS sent
// two polls per interval. Tailwind's `md` breakpoint is 768 px.
const DESKTOP_QUERY = '(min-width: 768px)';
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e: { matches: boolean }) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    // Safari < 14 has only the older addListener API.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return isDesktop;
}

/**
 * V14.5 Workspace Layout - PERMISSION-BASED ROUTING (Mobile-Responsive)
 *
 * **V7.0 DYNAMIC RBAC**: This component uses V7.0 Dynamic RBAC permissions
 * instead of hard-coded role checks to determine workspace access.
 *
 * Workspace Selection Priority (V14.5 Update):
 * 1. Admin & Ops Workspace (workspace.admin_ops.access) - Highest priority for admins
 * 2. Cash & AR Management (workspace.finance.access) - Revenue recovery & collections
 * 3. Transportation Workspace (workspace.transportation.access) - TSO logistics workflow
 * 4. Executive Workspace (workspace.executive.access) - Analytics & reports only
 * 5. Sales Workspace (workspace.sales.access) - Simple, focused UI for sales reps
 * 6. Default/No Access - Fallback for users without workspace permissions
 *
 * **KEY BENEFIT**: Admins can now grant workspace access dynamically via the
 * V7.0 Role Management UI without changing user roles directly.
 *
 * **V7.2 P0 FIX**: Added safety net redirect for users without workspace permissions.
 * If a user lands on /dashboard but has no workspace.admin_ops.access, they will be
 * redirected to their default route based on their role.
 *
 * Features:
 * - Mobile: Hamburger menu with overlay sidebar
 * - Desktop: Collapsible sidebar (full width or icon-only)
 * - Main content adjusts margin based on sidebar state
 */
export default function Layout() {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { isDesktopSidebarOpen } = useLayout();
  // V17.98.1: destructure `user` from auth — Layout.tsx:106 and :161 reference
  // user?.role_name but it was never pulled from the hook. Silent crash
  // ("ReferenceError: user is not defined") fired for any user whose URL did
  // not match an explicit path prefix AND who lacks workspace.admin_ops.access
  // AND has no AR permissions (e.g. finance_manager hitting
  // /commercial/discount-approvals). Broken since V17.86 (2026-04-10).
  const { user, isOffline } = useAuth();
  const isDesktop = useIsDesktop();
  const location = useLocation();


  /**
   * V14.5: Safety Net Redirect
   *
   * If a user has NO workspace permissions, redirect them to their default route.
   * This prevents the "blank page" issue where users land on /dashboard
   * but don't have any workspace access permissions.
   *
   * V15.0 UPDATE: Scout routes (/scout/*) bypass workspace requirement
   * Stage 0 Scouting is a separate workflow that doesn't need workspace sidebar
   *
   * V16.0 UPDATE: Residential routes (/residential/*) bypass workspace requirement
   * Residential workflows (sales rep dashboard, team leader command center) don't require workspace permissions
   *
   * V17.246 UPDATE: Customer Service routes (/customer-service/*) bypass workspace requirement
   * CS agents typically hold only cs.* permissions, not workspace.* permissions
   */
  const hasAnyWorkspaceAccess =
    hasPermission('workspace.admin_ops.access') ||
    hasPermission('workspace.finance.access') ||
    hasPermission('workspace.transportation.access') ||
    hasPermission('workspace.executive.access') ||
    hasPermission('workspace.sales.access') ||
    hasPermission('ar.dashboard.leader.view') ||  // V17.28.FIX: AR Commercial Leaders
    hasPermission('ar.dashboard.officer.view') ||  // V17.28.FIX: AR Commercial Officers
    hasPermission('commercial.onboarding.ar_approve') ||  // V17.39: AR Onboarding Approval
    hasPermission('operations.driver') ||  // V17.32.1: Truck Drivers
    hasPermission('referrals.create') ||  // V17.32: Referral creators
    hasPermission('referrals.manage') ||  // V17.32: Referral managers
    hasAnyPermission(['analytics.view', 'reports.scout.view', 'reports.sales.view']) ||  // V17.44: Report viewers
    hasAnyPermission(['reports.transport_team.view_own', 'reports.transport_team.view_division', 'reports.transport_team.view_all', 'admin.transport_org.manage']);  // V17.253: Transport team workspace

  const isScoutRoute = location.pathname.startsWith('/scout');
  const isResidentialRoute = location.pathname.startsWith('/residential');
  const isLogisticsRoute = location.pathname.startsWith('/logistics');
  const isFleetRoute = location.pathname.startsWith('/fleet');
  const isARCommercialRoute = location.pathname.startsWith('/ar-commercial');
  const isARRoute = location.pathname.startsWith('/ar/');  // V17.28.FIX: AR Officer routes (/ar/commercial/officer)
  const isCommercialRoute = location.pathname.startsWith('/commercial');  // V17.28.1: Commercial scouts
  const isApprovalsRoute = location.pathname.startsWith('/approvals');  // V17.39: Approval queues
  const isOperationsRoute = location.pathname.startsWith('/operations');  // V17.32.1: Driver routes
  const isReferralsRoute = location.pathname.startsWith('/referrals');  // V17.32: Referral routes
  const isReportsRoute = location.pathname.startsWith('/reports');  // V17.44: Report routes
  const isCustomerServiceRoute = location.pathname.startsWith('/customer-service');  // V17.246: CS ticket routes

  if (!hasAnyWorkspaceAccess && !isScoutRoute && !isResidentialRoute && !isLogisticsRoute && !isFleetRoute && !isARCommercialRoute && !isARRoute && !isCommercialRoute && !isApprovalsRoute && !isOperationsRoute && !isReferralsRoute && !isReportsRoute && !isCustomerServiceRoute) {
    // Row 43: RefuseTo renders the no-access view when the default page IS this page
    // (Head of Service Team on /dashboard) — <Navigate> to itself rendered a blank screen.
    return <RefuseTo />;
  }

  return (
    // V17.37: Dark mode support
    <div className="flex h-dvh-safe w-full overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* Mobile Header (hamburger menu) - Fixed position overlay. V17.520 (F29): mounted on phones only. */}
      {!isDesktop && <MobileHeader />}

      {/* 1. SIDEBAR - Fixed on Mobile, Flex Item on Desktop */}
      {/* V17.523 (Phase 1): ONE sidebar on every staff page — the menu no longer depends on the URL. */}
      <AppSidebar isDesktop={isDesktop} />

      {/* 2. MAIN CONTENT WRAPPER - This is the scrollable window */}
      {/* V17.59: isolate creates stacking context so map z-indexes (up to 9999) stay below sidebar overlay (z-1100+) */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative isolate">
        {/* Desktop Header - Inside main content area. V17.520 (F29): mounted on desktop only. */}
        {isDesktop && <Header />}

        {/* V17.520 (F01): offline notice under the header. On phones the fixed 64 px
            MobileHeader overlays the column top, so the banner starts below it. */}
        {isOffline && (
          <div className="mt-16 md:mt-0">
            <OfflineBanner />
          </div>
        )}

        {/* 3. SCROLLABLE AREA - The actual scrolling happens here */}
        {/* V17.37: Dark mode support */}
        <main className={`flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 ${isOffline ? 'pt-4' : 'pt-16'} md:pt-6 dark:text-gray-100`}>
          {/* RENDER CHILDREN HERE. V17.523 (F09): pages are lazy — while one loads, the spinner
              shows in the page area and the header + sidebar stay. Brief 2026-10-30-006: a page
              chunk gone after a deploy reloads once, then shows a message here; a new path clears it. */}
          <ChunkErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<BootLoading />}>
              <Outlet />
            </Suspense>
          </ChunkErrorBoundary>
        </main>

      </div>
    </div>
  );
}
